import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation, useRouteError } from 'react-router-dom'

import SigninPage from '../pages/sign-in/SigninPage'

import ChangePasswordPage from '@/pages/change-password/ChangePasswordPage'
import DashboardPage from '@/pages/dashboard/DashboardPage'
import DepartmentPage from '@/pages/departments/DepartmentPage'
import FloorMapsPage from '@/pages/floormaps/FloorMapsPage'
import PatientsPage from '@/pages/patients/PatientsPage'
import RepairsPage from '@/pages/repairs/RepairsPage'
import RequestsHistoryPage from '@/pages/requests-history/RequestsHistoryPage'
import RequestsPage from '@/pages/requests/RequestsPage'
import SettingsPage from '@/pages/settings/SettingsPage'
import TrackLocationsPage from '@/pages/track-locations/TrackLocationsPage'
import UsersPage from '@/pages/users/UsersPage'
import VendorsPage from '@/pages/vendors/VendorsPage'
import { MainLayout } from '../layouts/MainLayout'
import EquipmentPage from '../pages/equipment/EquipmentPage'

import { CHANGE_PASSWORD_PATH, getSafeReturnTo, getSignInUrl } from '@/features/auth/return-url'
import { ScanLayout } from '@/layouts/ScanLayout'
import ScanBedPage from '@/pages/scan/ScanBedPage'
import ScanItemPage from '@/pages/scan/ScanItemPage'
import ScanPatientPage from '@/pages/scan/ScanPatientPage'

import RequestQuoatationPage from '@/pages/quote/QuotationPage'

import { Button } from '@/components/ui/button'
import { Home, RefreshCcw, ShieldAlert } from 'lucide-react'

import { ADMIN_ROLES, QUOTE_ROLES, REPAIR_ROLES, STAFF_ROLES } from '@/config/permission'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { UserRole } from '@/features/users/types/user'
import DepartmentsPage from '@/pages/departments/DepartmentsPage'
import EquipmentItemsPage from '@/pages/equipment/EquipmentItemsPage'
import { useAuthStore } from '@/stores/useAuthStore'
import { useTranslation } from 'react-i18next'

function ErrorBoundary() {
  const { t } = useTranslation()
  const error = useRouteError() as { message?: string; statusText?: string }
  console.error(error)

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
      <div className="relative mb-8">
        <div className="absolute inset-0 bg-destructive/10 blur-3xl rounded-full scale-150" />
        <div className="relative bg-white h-32 w-32 rounded-[40px] shadow-soft flex items-center justify-center">
          <ShieldAlert className="h-16 w-16 text-destructive" />
        </div>
      </div>
      <div className="max-w-md space-y-4">
        <h1 className="text-4xl font-black text-slate-900 tracking-tight">{t('common.unexpectedError.title')}</h1>
        <p className="text-slate-500 text-lg font-medium leading-relaxed">{t('common.unexpectedError.description')}</p>
        <div className="bg-destructive/10 text-destructive p-4 rounded-2xl font-mono text-sm break-all">
          {error.statusText || error.message}
        </div>
      </div>

      <div className="flex flex-col gap-4 mt-10">
        <Button
          onClick={() => window.location.reload()}
          className="h-14 px-10 rounded-2xl font-black text-white bg-destructive shadow-soft hover:bg-destructive transition-all active:scale-95 flex gap-2"
        >
          <RefreshCcw className="h-5 w-5" />
          {t('common.unexpectedError.refreshPage')}
        </Button>
        <Button
          onClick={() => (window.location.href = '/')}
          className=" h-14 px-10 rounded-2xl font-black text-white bg-destructive shadow-soft hover:bg-destructive transition-all active:scale-95 flex gap-2"
        >
          <Home className="h-5 w-5" />
          {t('common.unexpectedError.homePage')}
        </Button>
      </div>
    </div>
  )
}

function GuestRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated())
  const currentUser = useAuthStore(s => s.currentUser)
  const location = useLocation()

  if (isAuthenticated) {
    if (currentUser?.isPasswordChanged === false) {
      return <Navigate to={CHANGE_PASSWORD_PATH} replace />
    }

    return <Navigate to={getSafeReturnTo(location.search)} replace />
  }

  return <>{children}</>
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated())
  const { currentUser } = useAuth()
  const location = useLocation()

  if (!isAuthenticated) {
    return <Navigate to={getSignInUrl()} replace />
  }

  if (currentUser?.isPasswordChanged === false && location.pathname !== CHANGE_PASSWORD_PATH) {
    return <Navigate to={CHANGE_PASSWORD_PATH} replace />
  }

  return <>{children}</>
}

function RoleRoute({ allowedRoles }: { allowedRoles: UserRole[] }) {
  const { currentUser } = useAuth()

  if (!currentUser || !allowedRoles.includes(currentUser.role)) {
    return <Navigate to="/" replace />
  }

  return <Outlet />
}

// function ProtectedRoute({ children, allowedRoles }: { children: React.ReactNode; allowedRoles?: UserRole[] }) {
//   const isAuthenticated = useAuthStore(s => s.isAuthenticated())
//   const { currentUser } = useAuth()
//   const location = useLocation()

//   if (!isAuthenticated) {
//     return <Navigate to={getSignInUrl()} replace />
//   }

//   if (currentUser?.isPasswordChanged === false && location.pathname !== CHANGE_PASSWORD_PATH) {
//     return <Navigate to={CHANGE_PASSWORD_PATH} replace />
//   }

//   if (allowedRoles && (!currentUser || !allowedRoles.includes(currentUser.role))) {
//     return <Navigate to="/" replace />
//   }

//   return <>{children}</>
// }

const router = createBrowserRouter([
  {
    path: '/sign-in',
    element: (
      <GuestRoute>
        <SigninPage />
      </GuestRoute>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'change-password', element: <ChangePasswordPage /> },
      {
        element: <RoleRoute allowedRoles={STAFF_ROLES} />,
        children: [
          { path: 'equipment', element: <EquipmentPage /> },
          { path: 'equipment/:equipmentId/items', element: <EquipmentItemsPage /> },
          { path: 'departments', element: <DepartmentsPage /> },
          { path: 'departments/:departmentId', element: <DepartmentPage /> },
          { path: 'floormaps', element: <FloorMapsPage /> },
          { path: 'patients', element: <PatientsPage /> },
          { path: 'requests', element: <RequestsPage /> },
          { path: 'requests-history', element: <RequestsHistoryPage /> },
        ],
      },
      {
        element: <RoleRoute allowedRoles={REPAIR_ROLES} />,
        children: [{ path: 'repairs', element: <RepairsPage /> }],
      },
      {
        element: <RoleRoute allowedRoles={ADMIN_ROLES} />,
        children: [
          { path: 'settings', element: <SettingsPage /> },
          { path: 'users', element: <UsersPage /> },
          { path: 'vendors', element: <VendorsPage /> },
          { path: 'track-locations', element: <TrackLocationsPage /> },
        ],
      },
    ],
  },
  {
    path: '/scan',
    element: (
      <ProtectedRoute>
        <ScanLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      {
        element: <RoleRoute allowedRoles={STAFF_ROLES} />,
        children: [
          { path: 'equipment/:itemId', element: <ScanItemPage /> },
          { path: 'bed/:departmentId/:bedId', element: <ScanBedPage /> },
          { path: 'patient/:patientId', element: <ScanPatientPage /> },
        ],
      },
    ],
  },
  {
    path: '/quote',
    element: (
      <ProtectedRoute>
        <ScanLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      {
        element: <RoleRoute allowedRoles={QUOTE_ROLES} />,
        children: [{ path: ':quatationId', element: <RequestQuoatationPage /> }],
      },
    ],
  },
])

export function AppRouter() {
  return <RouterProvider router={router} />
}
